import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminShared.css";
import { API_BASE_URL } from "../config";

const TYPES = [
  { key: "terms", label: "Terms & Conditions", icon: "📋" },
  { key: "privacy", label: "Privacy Policy", icon: "🔒" },
];

const EMPTY_FORM = { title: "", content: "" };

export default function AdminLegalContent() {
  const navigate = useNavigate();

  const [activeType, setActiveType] = useState("terms");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Panel state: null | "create" | "edit"
  const [panel, setPanel] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Preview modal
  const [preview, setPreview] = useState(null);

  const token = () => {
    const info = localStorage.getItem("adminInfo");
    return info ? JSON.parse(info).token : null;
  };

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token()}`,
  });

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const info = localStorage.getItem("adminInfo");
      const tkn = info ? JSON.parse(info).token : null;
      const res = await fetch(
        `${API_BASE_URL}/api/legal?type=${activeType}`,
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${tkn}` } }
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setItems(data.success ? data.data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [activeType]);

  useEffect(() => {
    const adminInfo = localStorage.getItem("adminInfo");
    if (!adminInfo) return navigate("/admin");
    fetchItems();
  }, [fetchItems, navigate]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError("");
    setEditTarget(null);
    setPanel("create");
  };

  const openEdit = (item) => {
    setForm({ title: item.title, content: item.content });
    setFormError("");
    setEditTarget(item);
    setPanel("edit");
  };

  const closePanel = () => {
    setPanel(null);
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  const handleSubmit = async () => {
    setFormError("");
    if (!form.title.trim() || form.title.trim().length < 2) {
      setFormError("Title must be at least 2 characters.");
      return;
    }
    if (!form.content.trim() || form.content.trim().length < 10) {
      setFormError("Content must be at least 10 characters.");
      return;
    }

    try {
      setSubmitting(true);
      let res;
      if (panel === "create") {
        res = await fetch(`${API_BASE_URL}/api/legal`, {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({
            type: activeType,
            title: form.title.trim(),
            content: form.content.trim(),
          }),
        });
      } else {
        res = await fetch(`${API_BASE_URL}/api/legal/${editTarget._id}`, {
          method: "PUT",
          headers: authHeaders(),
          body: JSON.stringify({
            title: form.title.trim(),
            content: form.content.trim(),
          }),
        });
      }
      const data = await res.json();
      if (!data.success) {
        setFormError(data.message || "Operation failed.");
        return;
      }
      closePanel();
      fetchItems();
    } catch {
      setFormError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleActivate = async (id) => {
    try {
      setActionLoading(id + "_activate");
      const res = await fetch(`${API_BASE_URL}/api/legal/${id}/activate`, {
        method: "PATCH",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!data.success) return alert("❌ " + (data.message || "Activation failed"));
      fetchItems();
    } catch {
      alert("❌ Network error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (item) => {
    if (item.isActive) {
      alert("⚠️ Cannot delete the active version. Activate another version first.");
      return;
    }
    if (!window.confirm(`Delete version ${item.version}? This cannot be undone.`)) return;
    try {
      setActionLoading(item._id + "_delete");
      const res = await fetch(`${API_BASE_URL}/api/legal/${item._id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!data.success) return alert("❌ " + (data.message || "Delete failed"));
      fetchItems();
    } catch {
      alert("❌ Network error");
    } finally {
      setActionLoading(null);
    }
  };

  const activeItem = items.find((i) => i.isActive);

  if (loading)
    return (
      <div className="admin-list-container">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "80vh",
            gap: "20px",
          }}
        >
          <div className="loading-spinner"></div>
          <p style={{ fontWeight: 700, color: "#475569" }}>
            ⏳ Loading Legal Content...
          </p>
        </div>
      </div>
    );

  return (
    <div className="admin-list-container">
      <div className="admin-list-content">
        {/* ── Page Title ── */}
        <div className="page-title-section">
          <h1>Legal Content Manager</h1>
          <p>Manage versioned Terms & Conditions and Privacy Policy documents.</p>
        </div>

        {/* ── Type Tabs ── */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "28px" }}>
          {TYPES.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveType(t.key)}
              style={{
                padding: "12px 28px",
                borderRadius: "14px",
                border: activeType === t.key ? "2px solid #6366f1" : "2px solid #e2e8f0",
                background: activeType === t.key ? "#6366f1" : "#fff",
                color: activeType === t.key ? "#fff" : "#64748b",
                fontWeight: 800,
                fontSize: "0.9rem",
                cursor: "pointer",
                transition: "all 0.2s ease",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* ── Info Bar ── */}
        <div className="admin-info-bar">
          <div className="admin-user-tag">
            <div
              className="dot"
              style={{ background: activeItem ? "#10b981" : "#f59e0b" }}
            ></div>
            {activeItem
              ? `Active: v${activeItem.version} — ${activeItem.title}`
              : "No active version"}
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <div
              className="admin-status-badge"
              style={{
                background: "#f0f9ff",
                color: "#0369a1",
                fontSize: "0.75rem",
                fontWeight: 800,
                padding: "8px 16px",
                borderRadius: "10px",
              }}
            >
              {items.length} VERSION{items.length !== 1 ? "S" : ""}
            </div>
            <button
              className="module-badge"
              style={{ cursor: "pointer", border: "none" }}
              onClick={openCreate}
            >
              + New Version
            </button>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="admin-table-wrapper" style={{ overflowX: "auto" }}>
          <table className="elite-table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Title</th>
                <th>Created By</th>
                <th>Created At</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      textAlign: "center",
                      padding: "80px",
                      color: "#94a3b8",
                    }}
                  >
                    No versions found. Create the first one!
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          background: item.isActive ? "#6366f1" : "#f1f5f9",
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: "0.85rem",
                          color: item.isActive ? "#fff" : "#64748b",
                        }}
                      >
                        v{item.version}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 700 }}>{item.title}</div>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#94a3b8",
                          marginTop: 2,
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.content.slice(0, 80)}…
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {item.createdBy?.name || "Admin"}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                        {item.createdBy?.email || "—"}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {new Date(item.createdAt).toLocaleDateString("en-GB")}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                        {new Date(item.createdAt).toLocaleTimeString()}
                      </div>
                    </td>
                    <td>
                      <span
                        className="status-tag"
                        style={
                          item.isActive
                            ? { background: "#dcfce7", color: "#166534" }
                            : { background: "#f1f5f9", color: "#475569" }
                        }
                      >
                        {item.isActive ? "✅ ACTIVE" : "INACTIVE"}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px" }}>
                        {/* Preview */}
                        <button
                          className="action-btn-elite"
                          style={{
                            background: "rgba(14,165,233,0.08)",
                            color: "#0ea5e9",
                            border: "1px solid rgba(14,165,233,0.15)",
                          }}
                          onClick={() => setPreview(item)}
                          title="Preview"
                        >
                          👁
                        </button>

                        {/* Edit */}
                        <button
                          className="action-btn-elite btn-update"
                          onClick={() => openEdit(item)}
                          title="Edit"
                        >
                          ✏️
                        </button>

                        {/* Activate */}
                        {!item.isActive && (
                          <button
                            className="action-btn-elite"
                            style={{
                              background: "rgba(16,185,129,0.08)",
                              color: "#10b981",
                              border: "1px solid rgba(16,185,129,0.15)",
                            }}
                            onClick={() => handleActivate(item._id)}
                            disabled={actionLoading === item._id + "_activate"}
                            title="Set as Active"
                          >
                            {actionLoading === item._id + "_activate"
                              ? "..."
                              : "✓ Activate"}
                          </button>
                        )}

                        {/* Delete */}
                        <button
                          className="action-btn-elite btn-delete"
                          onClick={() => handleDelete(item)}
                          disabled={
                            item.isActive ||
                            actionLoading === item._id + "_delete"
                          }
                          title={
                            item.isActive
                              ? "Cannot delete active version"
                              : "Delete"
                          }
                          style={item.isActive ? { opacity: 0.35 } : {}}
                        >
                          {actionLoading === item._id + "_delete" ? "..." : "🗑️"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Create / Edit Side Panel ── */}
      {panel && (
        <div className="admin-form-overlay" onClick={closePanel}>
          <div
            className="admin-side-panel"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 560, display: "flex", flexDirection: "column" }}
          >
            {/* Panel Header */}
            <div
              style={{
                padding: "28px 32px 20px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontWeight: 800,
                    fontSize: "1.25rem",
                    color: "#0f172a",
                  }}
                >
                  {panel === "create" ? "➕ New Version" : "✏️ Edit Version"}
                </h2>
                <p style={{ margin: "4px 0 0", fontSize: "0.85rem", color: "#94a3b8" }}>
                  {TYPES.find((t) => t.key === activeType)?.label}
                  {panel === "edit" && editTarget
                    ? ` — v${editTarget.version}`
                    : ""}
                </p>
              </div>
              <button
                onClick={closePanel}
                style={{
                  background: "#f1f5f9",
                  border: "none",
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ✕
              </button>
            </div>

            {/* Panel Body */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "28px 32px",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              {/* Title */}
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: 8,
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Document Title
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, title: e.target.value }))
                  }
                  placeholder="e.g. Terms & Conditions v3"
                  style={{
                    width: "100%",
                    padding: "13px 16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    fontFamily: "inherit",
                    fontSize: "1rem",
                    background: "#f8fafc",
                    boxSizing: "border-box",
                    color: "#1e293b",
                    outline: "none",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
              </div>

              {/* Content */}
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 8,
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Content (HTML or plain text)
                </label>
                <textarea
                  value={form.content}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, content: e.target.value }))
                  }
                  placeholder="Enter the full legal content here..."
                  rows={16}
                  style={{
                    width: "100%",
                    padding: "13px 16px",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                    background: "#f8fafc",
                    boxSizing: "border-box",
                    color: "#1e293b",
                    resize: "vertical",
                    outline: "none",
                    lineHeight: 1.6,
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
                <div style={{ fontSize: "0.75rem", color: "#94a3b8", marginTop: 6 }}>
                  {form.content.length} characters
                </div>
              </div>

              {/* Error */}
              {formError && (
                <div
                  style={{
                    background: "#fef2f2",
                    border: "1px solid #fecaca",
                    color: "#dc2626",
                    borderRadius: "10px",
                    padding: "12px 16px",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                  }}
                >
                  ⚠️ {formError}
                </div>
              )}
            </div>

            {/* Panel Footer */}
            <div
              style={{
                padding: "20px 32px 28px",
                borderTop: "1px solid #f1f5f9",
                display: "flex",
                gap: "12px",
                flexShrink: 0,
              }}
            >
              <button
                onClick={closePanel}
                style={{
                  flex: 1,
                  padding: "14px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  color: "#64748b",
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
              <button
                className="btn-primary-elite"
                onClick={handleSubmit}
                disabled={submitting}
                style={{ flex: 2, borderRadius: "12px", fontSize: "0.95rem" }}
              >
                {submitting
                  ? "Saving..."
                  : panel === "create"
                  ? "Create Version"
                  : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Preview Modal ── */}
      {preview && (
        <div
          className="admin-form-overlay"
          style={{ justifyContent: "center", alignItems: "center" }}
          onClick={() => setPreview(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "20px",
              width: "90%",
              maxWidth: 720,
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
              overflow: "hidden",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "24px 28px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexShrink: 0,
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <h3 style={{ margin: 0, fontWeight: 800, fontSize: "1.1rem" }}>
                    {preview.title}
                  </h3>
                  <span
                    className="status-tag"
                    style={
                      preview.isActive
                        ? { background: "#dcfce7", color: "#166534" }
                        : { background: "#f1f5f9", color: "#475569" }
                    }
                  >
                    {preview.isActive ? "ACTIVE" : "INACTIVE"}
                  </span>
                </div>
                <p style={{ margin: "6px 0 0", fontSize: "0.8rem", color: "#94a3b8" }}>
                  Version {preview.version} ·{" "}
                  {new Date(preview.createdAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </div>
              <button
                onClick={() => setPreview(null)}
                style={{
                  background: "#f1f5f9",
                  border: "none",
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontSize: "1.1rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                ✕
              </button>
            </div>
            {/* Modal Body */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "28px",
                fontSize: "0.9rem",
                lineHeight: 1.8,
                color: "#334155",
                whiteSpace: "pre-wrap",
              }}
              dangerouslySetInnerHTML={{ __html: preview.content }}
            />
          </div>
        </div>
      )}
    </div>
  );
}